/**
 * 세무관리 — 원천세 급여대장 업로드 + 필드매핑
 * 회사마다 다른 엑셀 헤더를 표준 필드에 매핑(규칙기반 제안) 후 담당자가 확정한다.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W, theme } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import { clientOptions, loadClients } from 'tax/clientCompanyStore';
import {
  MONTH_OPTIONS,
  WHT_FIELD_OPTIONS,
  YN_OPTIONS,
  payYm,
  emptyPayrollLine,
  nextSortOrderBy10,
  createUid,
  loadMappings,
  persistMappings,
  loadLines,
  persistLines,
  loadUploadMeta,
  persistUploadMeta,
  suggestFieldMapping,
  firstHeaderRow,
  readExcelAoaFromArrayBuffer,
  applyMappingToAoa,
  aggregatePayroll,
  downloadPayrollTemplate,
} from 'tax/whtPayrollStore';

const MAP_GRID_ID = 'tx-wht-mapping';
const LINE_GRID_ID = 'tx-wht-payroll-line';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || 1};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Meta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
  flex-shrink: 0;
`;

function currentYear() {
  return String(new Date().getFullYear());
}

function WhtPayrollUploadInner() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const fileInputRef = useRef(null);
  const mapRef = useRef(null);
  const lineRef = useRef(null);
  const rawAoaRef = useRef([]);
  const [hint, setHint] = useState('고객사·귀속연월을 선택한 뒤 조회하거나 엑셀을 업로드하세요.');
  const [clientTick, setClientTick] = useState(0);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({});

  const clientOpts = useMemo(() => {
    loadClients(tenantCode);
    return clientOptions(tenantCode, { includeAll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantCode, clientTick]);

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      clientCode: clientOpts.find((o) => o.value)?.value || '',
      fiscalYear: currentYear(),
      month: '08',
    }),
    [tenantCode, clientOpts]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  useEffect(() => {
    if (!getValues('clientCode') && searchDefaults.clientCode) {
      setValue('clientCode', searchDefaults.clientCode);
    }
  }, [getValues, setValue, searchDefaults.clientCode]);

  const mapColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '엑셀헤더', field: 'excelHeader', size: 160, required: true }),
      hiCol.edComboColumn({
        header: '표준필드',
        field: 'standardField',
        size: 140,
        options: WHT_FIELD_OPTIONS,
      }),
      hiCol.roNumberColumn({ header: '신뢰도', field: 'confidence', size: 80, align: 'center' }),
      hiCol.edComboColumn({ header: '확정', field: 'confirmedYn', size: 72, options: YN_OPTIONS }),
      hiCol.edNumberColumn({ header: '컬럼', field: 'excelCol', size: 70, align: 'center' }),
    ],
    []
  );

  const lineColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '사번', field: 'empNo', size: 90 }),
      hiCol.edTextColumn({ header: '성명', field: 'empName', size: 100, required: true }),
      hiCol.edTextColumn({ header: '주민등록번호', field: 'rrn', size: 130, required: true }),
      hiCol.edTextColumn({ header: '부서', field: 'deptName', size: 100 }),
      hiCol.edNumberColumn({ header: '과세소득', field: 'taxableIncome', size: 110 }),
      hiCol.edNumberColumn({ header: '비과세', field: 'nontaxable', size: 100 }),
      hiCol.edNumberColumn({ header: '부양가족', field: 'dependents', size: 90, align: 'center' }),
      hiCol.edNumberColumn({ header: '국민연금', field: 'nationalPension', size: 100 }),
      hiCol.edNumberColumn({ header: '건강보험', field: 'healthIns', size: 100 }),
      hiCol.edNumberColumn({ header: '고용보험', field: 'empIns', size: 100 }),
      hiCol.edNumberColumn({ header: '소득세', field: 'incomeTax', size: 100 }),
      hiCol.edNumberColumn({ header: '지방소득세', field: 'localTax', size: 110 }),
    ],
    []
  );

  const mapGrid = useHiGridHook({
    gridRef: mapRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      excelHeader: '',
      standardField: '',
      confidence: 0,
      confirmedYn: 'N',
      excelCol: 0,
      sortOrder: 10,
    },
  });

  const lineGrid = useHiGridHook({
    gridRef: lineRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyPayrollLine(),
  });

  const requireScope = useCallback((opts = {}) => {
    const clientCode = String(getValues('clientCode') || '').trim();
    const year = getValues('fiscalYear');
    const month = getValues('month');
    const ym = payYm(year, month);
    if (!clientCode) {
      if (!opts.silent) {
        showAlert('고객사를 선택하세요. 없으면 세무관리 > 기업등록에서 먼저 등록합니다.', 'warning');
      }
      return null;
    }
    if (!ym || ym.length !== 6) {
      if (!opts.silent) showAlert('귀속 연·월을 확인하세요.', 'warning');
      return null;
    }
    return { clientCode, ym };
  }, [getValues]);

  const handleSearch = useCallback(async (opts = {}) => {
    const scope = requireScope(opts);
    if (!scope) return;
    try {
      setClientTick((n) => n + 1);
      const maps = loadMappings(tenantCode, scope.clientCode);
      const lines = loadLines(tenantCode, scope.clientCode, scope.ym);
      const meta = loadUploadMeta(tenantCode, scope.clientCode, scope.ym);
      mapRef.current?.setData?.(maps);
      lineRef.current?.setData?.(lines);
      setHint(
        meta?.fileName
          ? `${meta.fileName} · 매핑 ${maps.length} · 라인 ${lines.length}${
              meta.mappingConfirmedYn === 'Y' ? ' · 매핑확정' : ''
            }`
          : `저장분 매핑 ${maps.length} · 라인 ${lines.length}`
      );
    } catch (e) {
      showAlert(e?.message || '조회 실패', 'error');
    }
  }, [requireScope, tenantCode]);

  useEffect(() => {
    handleSearch({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantCode]);

  const handleMapAdd = useCallback(async () => {
    const data = mapRef.current?.getData?.() || [];
    await mapGrid.handleAdd({
      mapUid: createUid('TM'),
      excelCol: data.length,
      sortOrder: nextSortOrderBy10(data),
      confirmedYn: 'N',
      confidence: 0,
    });
  }, [mapGrid]);

  const handleLineAdd = useCallback(async () => {
    const data = lineRef.current?.getData?.() || [];
    await lineGrid.handleAdd({
      ...emptyPayrollLine({ lineUid: createUid('TL'), sortOrder: nextSortOrderBy10(data), mod: 'A' }),
    });
  }, [lineGrid]);

  const handleSave = useCallback(async () => {
    const scope = requireScope();
    if (!scope) return;
    try {
      const maps = mapRef.current?.getData?.() || [];
      const lines = lineRef.current?.getData?.() || [];
      const savedMaps = persistMappings(tenantCode, scope.clientCode, maps);
      const savedLines = persistLines(tenantCode, scope.clientCode, scope.ym, lines);
      const prev = loadUploadMeta(tenantCode, scope.clientCode, scope.ym) || {};
      persistUploadMeta(tenantCode, scope.clientCode, scope.ym, {
        ...prev,
        fileName: prev.fileName || '',
        mappingConfirmedYn: prev.mappingConfirmedYn || 'N',
      });
      mapRef.current?.setData?.(savedMaps);
      lineRef.current?.setData?.(savedLines);
      showAlert('저장했습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '저장 실패', 'error');
    }
  }, [requireScope, tenantCode]);

  const handleUploadClick = useCallback(() => {
    if (!requireScope()) return;
    fileInputRef.current?.click();
  }, [requireScope]);

  const handleUploadFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      const scope = requireScope();
      if (!scope) return;
      try {
        const buf = await file.arrayBuffer();
        const aoa = readExcelAoaFromArrayBuffer(buf);
        rawAoaRef.current = aoa;
        const { headers } = firstHeaderRow(aoa);
        if (!headers.some((h) => String(h).trim())) {
          showAlert('엑셀 헤더를 찾지 못했습니다.', 'warning');
          return;
        }
        const saved = loadMappings(tenantCode, scope.clientCode);
        const suggested = suggestFieldMapping(headers);
        // 고객사에 저장된 매핑이 있으면 헤더명 기준으로 재사용
        const byHeader = new Map(
          (saved || []).map((m) => [String(m.excelHeader || '').trim(), m])
        );
        const maps = suggested.map((m) => {
          const prev = byHeader.get(String(m.excelHeader || '').trim());
          if (!prev) return m;
          return {
            ...m,
            standardField: prev.standardField,
            confirmedYn: prev.confirmedYn || 'Y',
            confidence: 100,
            mapUid: prev.mapUid || m.mapUid,
            mod: 'U',
          };
        });
        const lines = applyMappingToAoa(aoa, maps);
        mapRef.current?.setData?.(maps);
        lineRef.current?.setData?.(lines);
        persistUploadMeta(tenantCode, scope.clientCode, scope.ym, {
          fileName: file.name,
          uploadedAt: new Date().toISOString(),
          mappingConfirmedYn: 'N',
          rowCount: lines.length,
        });
        setHint(`${file.name} · 헤더 ${headers.length} · 라인 ${lines.length} — 매핑을 확인하세요.`);
        showAlert(`업로드 ${lines.length}건. 표준필드 매핑을 확인한 뒤 저장하세요.`, 'success');
      } catch (err) {
        showAlert(`엑셀 읽기 실패: ${err?.message || err}`, 'error');
      }
    },
    [requireScope, tenantCode]
  );

  const handleSuggest = useCallback(() => {
    const maps = mapRef.current?.getData?.() || [];
    const headers = maps.map((m) => m.excelHeader);
    if (!headers.length) {
      showAlert('매핑할 엑셀 헤더가 없습니다. 먼저 엑셀을 업로드하세요.', 'warning');
      return;
    }
    const suggested = suggestFieldMapping(headers);
    mapRef.current?.setData?.(
      suggested.map((s, i) => ({
        ...maps[i],
        ...s,
        mapUid: maps[i]?.mapUid || s.mapUid,
        excelCol: maps[i]?.excelCol ?? s.excelCol,
        mod: maps[i]?.mod === 'A' ? 'A' : 'U',
      }))
    );
    showAlert('매핑을 다시 제안했습니다. 확인 후 저장하세요.');
  }, []);

  const handleConfirmMapping = useCallback(async () => {
    const scope = requireScope();
    if (!scope) return;
    const maps = mapRef.current?.getData?.() || [];
    if (!maps.filter((m) => m.mod !== 'D' && m.standardField).length) {
      showAlert('표준필드가 지정된 매핑이 없습니다.', 'warning');
      return;
    }
    const nextMaps = maps.map((m) => ({
      ...m,
      confirmedYn: m.standardField ? 'Y' : 'N',
      mod: m.mod === 'A' ? 'A' : 'U',
    }));
    if (rawAoaRef.current?.length) {
      const lines = applyMappingToAoa(rawAoaRef.current, nextMaps);
      lineRef.current?.setData?.(lines);
    }
    mapRef.current?.setData?.(nextMaps);
    persistMappings(tenantCode, scope.clientCode, nextMaps);
    const prev = loadUploadMeta(tenantCode, scope.clientCode, scope.ym) || {};
    persistUploadMeta(tenantCode, scope.clientCode, scope.ym, { ...prev, mappingConfirmedYn: 'Y' });
    showAlert('매핑을 확정했습니다. 다음 달부터 같은 헤더는 재사용됩니다.', 'success');
  }, [requireScope, tenantCode]);

  const handleAggregate = useCallback(async () => {
    const scope = requireScope();
    if (!scope) return;
    const lines = (lineRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
    if (!lines.length) {
      showAlert('집계할 급여 라인이 없습니다.', 'warning');
      return;
    }
    const missing = lines.filter((r) => !String(r.empName || '').trim() || !String(r.rrn || '').trim());
    if (missing.length) {
      showAlert(`성명·주민번호 누락 ${missing.length}건이 있습니다.`, 'warning');
      return;
    }
    const ok = await showConfirm(`급여 ${lines.length}건을 원천징수 집계할까요?`);
    if (!ok) return;
    const { snapshot, lines: calcLines } = aggregatePayroll(
      tenantCode,
      scope.clientCode,
      scope.ym,
      lines
    );
    lineRef.current?.setData?.(calcLines);
    setHint(
      `집계 ${snapshot.count}명 · 지급 ${Number(snapshot.totalPay).toLocaleString('ko-KR')} · 소득세 ${Number(
        snapshot.totalIncomeTax
      ).toLocaleString('ko-KR')}`
    );
    showAlert('집계했습니다. 원천징수이행상황신고 화면에서 리포트·EDI를 확인하세요.', 'success');
  }, [requireScope, tenantCode]);

  const getMapData = useCallback(
    () => mapRef.current?.getFilteredData?.() ?? mapRef.current?.getData?.() ?? [],
    []
  );
  const getMapCols = useCallback(() => mapRef.current?.getDataColumns?.() ?? [], []);
  const getLineData = useCallback(
    () => lineRef.current?.getFilteredData?.() ?? lineRef.current?.getData?.() ?? [],
    []
  );
  const getLineCols = useCallback(() => lineRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: MAP_GRID_ID,
    label: '필드매핑',
    getData: getMapData,
    getDataColumns: getMapCols,
    order: 0,
  });
  useRegisterGridExport({
    id: LINE_GRID_ID,
    label: '급여라인',
    getData: getLineData,
    getDataColumns: getLineCols,
    order: 1,
  });
  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '원천세급여대장' });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '➖',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9)',
      },
      { id: 'save', label: '저장', icon: '💾', onClick: handleSave, hotkey: 'F10' },
      excelButton,
    ],
    [handleSearch, handleActiveAdd, handleActiveDelete, handleSave, excelButton, activeLabel]
  );

  const extraButtons = useMemo(
    () => [
      {
        id: 'tpl-a',
        label: '샘플A',
        icon: '📄',
        onClick: () => downloadPayrollTemplate('A'),
        title: '표준 헤더 샘플 엑셀',
      },
      {
        id: 'tpl-b',
        label: '샘플B',
        icon: '📄',
        onClick: () => downloadPayrollTemplate('B'),
        title: '다른 양식 샘플 엑셀',
      },
      {
        id: 'upload',
        label: '엑셀업로드',
        icon: '📤',
        onClick: handleUploadClick,
      },
      {
        id: 'suggest',
        label: '매핑제안',
        icon: '✨',
        onClick: handleSuggest,
        title: '헤더 기준으로 표준필드 재제안',
      },
      {
        id: 'confirm-map',
        label: '매핑확정',
        icon: '✔',
        onClick: handleConfirmMapping,
      },
      {
        id: 'agg',
        label: '집계',
        icon: '∑',
        onClick: handleAggregate,
        title: '세율표 마스터로 원천징수세액 산출',
      },
    ],
    [handleUploadClick, handleSuggest, handleConfirmMapping, handleAggregate]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'clientCode',
          label: '고객사',
          type: 'select',
          width: CommonCss.w200,
          labelWidth: SEARCH_LABEL_W,
          options: clientOpts,
        },
        {
          name: 'fiscalYear',
          label: '귀속연도',
          type: 'fiscalYear',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'month',
          label: '월',
          type: 'select',
          width: CommonCss.w90,
          labelWidth: SEARCH_LABEL_W,
          options: MONTH_OPTIONS,
        },
      ],
    ],
    [clientOpts]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <CollapsibleSearchPanel
          storageKey="tx-wht-payroll-upload"
          prefsUserId={prefsUserId}
          title="조회조건"
        >
          <FormArea compact fields={searchFields} onFieldEnter={handleSearch} />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성: <strong style={{ color: 'var(--erp-color-primary)' }}>{activeLabel || '—'}</strong>
        {' · '}
        Alt+F2 그리드 전환 · F8/F9는 활성 그리드
        {' · '}
        {hint}
      </Meta>

      <Stack>
        <Panel $flex={0.7}>
          <ActiveGridHeader gridId={MAP_GRID_ID} extra="헤더 → 표준필드 · 고객사별 재사용">
            필드매핑
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={MAP_GRID_ID}
              label="필드매핑"
              order={0}
              onAdd={handleMapAdd}
              onDelete={async () => mapGrid.handleDelete?.()}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={MAP_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={mapRef}
                  columns={mapColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === MAP_GRID_ID}
                  {...mapGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  enableFindReplace
                  enableCut
                  enableRowEditMenu
                  allowReplace
                  enableContextMenu
                  exportFileName="필드매핑"
                  gridId={MAP_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={1.3}>
          <ActiveGridHeader gridId={LINE_GRID_ID} extra="매핑 확정 후 정제된 급여 라인">
            급여라인
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={LINE_GRID_ID}
              label="급여라인"
              order={10}
              onAdd={handleLineAdd}
              onDelete={async () => lineGrid.handleDelete?.()}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={LINE_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={lineRef}
                  columns={lineColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === LINE_GRID_ID}
                  {...lineGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  enableFindReplace
                  enableCut
                  enableRowEditMenu
                  allowReplace
                  enableContextMenu
                  exportFileName="급여라인"
                  gridId={LINE_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleUploadFile}
      />
      {excelDialog}
    </PageContainer>
  );
}

export default function WhtPayrollUpload() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <WhtPayrollUploadInner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
